import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createStructuredSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationRootState } from '../../../../types';
import { makeSelectHaveOneDoor, makeSelectHaveOneDrawer } from '../selectors';
import {
  makeSelectShelvesLevelSelected,
  makeSelectShelvesSelected,
} from '../../../Configurator/selectors/selection';
import {
  makeSelectSelectedIdCabinet,
  makeSelectShelves,
} from '../../../Configurator/selectors/furniture';
import { makeSelectFullDrawersSelection } from '../../../DrawersPanel/selectors';
import { makeSelectCanAddIs } from '../ColorsSection/selectors';
import { makeSelectCounterSelectedISInShelvesSelected } from '../../../IntermediateShelfPanel/selectors';
import { Shelf } from '../../../../lib/modelBuilder/entities/Shelf';
import { useMedia } from 'use-media';
import RibbonSection from '../../RibbonSection';
import Button from '../../../../components/Button';
import { setColorsPanelAction } from '../../../Configurator/actions/general';
import { ActivePanel } from '../../../Configurator/constants/general';
import HandlesImg from '../../../../assets/images/ribbons/shelvesRibbon/handles.svg';
import { addIntermediateShelvesOnSelectionAction } from '../../../IntermediateShelfPanel/actions';
import { findColor } from '../../../../lib/modelBuilder/utils/colorHelper';
import {
  ShelfComposition,
  WHITE_COLOR,
} from '../../../../lib/modelBuilder/constants';
import addISImg from '../../../../assets/images/ribbons/shelvesRibbon/addIS.svg';
import ISImg from '../../../../assets/images/ribbons/shelvesRibbon/IS.svg';
import ShelvesOptions from '../ShelvesOptions';
import { makeSelectRules } from '../../../Configurator/selectors/rules';
import {
  makeSelectContext,
  makeSelectIsActiveCustomMode,
} from '../../../Configurator/selectors/general';
import { Rule } from '../../../../lib/constraintsChecker/Rule';
import {
  FurnitureEditor,
  FurnitureEditorContext,
  FurnitureEditorStatus,
} from '../../../../lib/constraintsChecker/FurnitureEditor';
import {
  makeSelectLastHistoryCabinet,
  makeSelectLastHistoryFurniture,
} from '../../../Configurator/selectors/history';
import { Cabinet } from '../../../../lib/modelBuilder/entities/Cabinet';
import { Furniture } from '../../../../lib/modelBuilder/entities/Furniture';
import { select } from 'redux-saga/effects';

// import styled from 'styled-components';

interface StateProps {
  haveOneDrawer: boolean;
  haveOneDoor: boolean;
  shelvesSelected: Shelf[];
  shelvesCabinet: Shelf[];
  isAllSelectionDrawers: boolean;
  canAddIs: boolean;
  counterIS: number | null;
  rules: Rule[] | null;
  context: FurnitureEditorContext;
  cabinet: Cabinet;
  customMode: boolean;
  shelvesLevelSelected: number[];
  furniture: Furniture;
  selectedIdCabinet: string | null;
}

const stateSelector = createStructuredSelector<
  ApplicationRootState,
  StateProps
>({
  haveOneDrawer: makeSelectHaveOneDrawer(),
  haveOneDoor: makeSelectHaveOneDoor(),
  shelvesSelected: makeSelectShelvesSelected(),
  shelvesCabinet: makeSelectShelves(),
  isAllSelectionDrawers: makeSelectFullDrawersSelection(),
  canAddIs: makeSelectCanAddIs(),
  counterIS: makeSelectCounterSelectedISInShelvesSelected(),
  rules: makeSelectRules(),
  context: makeSelectContext(),
  cabinet: makeSelectLastHistoryCabinet(),
  customMode: makeSelectIsActiveCustomMode(),
  shelvesLevelSelected: makeSelectShelvesLevelSelected(),
  furniture: makeSelectLastHistoryFurniture(),
  selectedIdCabinet: makeSelectSelectedIdCabinet(),
});

const AccessoriesSection = () => {
  const {
    haveOneDrawer,
    haveOneDoor,
    shelvesCabinet,
    shelvesSelected,
    isAllSelectionDrawers,
    canAddIs,
    counterIS,
    rules,
    context,
    cabinet,
    customMode,
    shelvesLevelSelected,
    furniture,
    selectedIdCabinet,
  } = useSelector(stateSelector);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isXLargeScreen = useMedia({ minWidth: 1280 });

  const showShelfActionMenu =
    shelvesCabinet.length > 1 && shelvesSelected.length === 1;
  const showIS = !isAllSelectionDrawers && canAddIs;
  const showHandles = haveOneDoor || haveOneDrawer;

  function checkIS(): FurnitureEditor | null {
    if (rules === null) {
      return null;
    }
    const cabinetNext = cabinet.clone();
    let selectedShelves: Shelf[] = [];
    if (shelvesLevelSelected.length === 0)
      selectedShelves = cabinetNext.shelves;
    else
      selectedShelves = cabinetNext.shelves.filter(item =>
        shelvesLevelSelected.includes(item.level),
      );

    const cabinetEditor = new FurnitureEditor(
      furniture,
      selectedIdCabinet,
      rules,
      context,
      customMode,
    );

    selectedShelves.map(shelf => shelf.addIntermediateShelf());

    cabinetEditor.checkRules(cabinetNext);
    if (cabinetEditor.status === FurnitureEditorStatus.Cancel)
      return cabinetEditor;
    return null;
  }

  const addISCA = useMemo(() => checkIS(), [cabinet, shelvesLevelSelected]);

  if (showIS || showHandles) {
    return (
      <>
        <div className="h-20 border-l border-gray-400" />
        <RibbonSection
          title={t('Accessories')}
          className="tuto-step-shelves-accessories"
          border={false}
        >
          <div
            className={` ${
              (showHandles && !showIS) || (showIS && !showHandles)
                ? 'mt-2'
                : 'mt-2'
            } flex flex-col justify-around h-full`}
          >
            {showHandles && (
              <Button
                title={t('Handles')}
                onClick={() => {
                  dispatch(setColorsPanelAction(ActivePanel.handles));
                }}
                img={HandlesImg}
                small={showIS}
                showImg={!showShelfActionMenu && isXLargeScreen}
                className=""
              />
            )}
            {showIS && (
              <Button
                title={t('Intermediates Shelves')}
                onClick={() => {
                  dispatch(setColorsPanelAction(ActivePanel.IntermediateShelf));
                  if (counterIS === 0) {
                    dispatch(
                      addIntermediateShelvesOnSelectionAction(
                        undefined,
                        undefined,
                        undefined,
                      ),
                    );
                  }
                }}
                img={counterIS === 0 ? addISImg : ISImg}
                small={showHandles}
                showImg={!showShelfActionMenu && isXLargeScreen}
                disable={!!addISCA}
                tooltip={addISCA && addISCA.message && addISCA.message.content}
              />
            )}
          </div>
        </RibbonSection>
      </>
    );
  }
  return null;
};

export default AccessoriesSection;
